import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Clock, Cpu, Loader2, XCircle } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import { AI_MODEL_OPTIONS } from "./aiModelSettings";

export interface TestImageResult {
  imageUrl: string;
  status: "loading" | "done" | "error" | "disabled";
  result?: string;
  reason?: string;
  message?: string;
  subject?: string;
  data?: Record<string, number>;
  model?: string | null;
  durationMs?: number;
}

export interface TestRun {
  id: string;
  model: string | null;
  startedAt: Date;
  results: TestImageResult[];
}

const modelLabel = (m?: string | null) => AI_MODEL_OPTIONS.find((o) => o.value === m)?.label ?? m ?? "No model";
const BAR_COLOURS = ["hsl(var(--primary))", "#9DB84A", "#C9A27E", "#4A9DB8", "#B84A6E"];

function ResultColumn({ r, index }: { r: TestImageResult; index: number }) {
  const isTrue = r.result?.toUpperCase() === "TRUE";
  const quality = r.subject === "Image too Dark" || r.subject === "Image too Blurry";
  const warn = r.status === "error" || r.status === "disabled" || quality;

  return (
    <div className="w-full rounded-lg border bg-background p-3 space-y-3">
      <div className="relative">
        <img src={r.imageUrl} alt={`Test image ${index + 1}`} className="h-32 w-full rounded-md object-cover border" />
        <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] font-medium px-1.5 py-0.5 rounded">{index + 1}</span>
      </div>

      {r.status === "loading" ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Analysing…
        </div>
      ) : warn ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold"><AlertTriangle className="h-4 w-4 text-destructive" />{r.subject || "Test failed"}</div>
          {r.reason && <p className="text-xs text-muted-foreground break-words">{r.reason}</p>}
        </div>
      ) : (
        <div className={cn("rounded-md p-3 flex items-center gap-2", isTrue ? "bg-destructive text-destructive-foreground" : "bg-success text-success-foreground")}>
          {isTrue ? <CheckCircle2 className="h-6 w-6 shrink-0" /> : <XCircle className="h-6 w-6 shrink-0" />}
          <div>
            <div className="text-lg font-bold leading-none">{isTrue ? "TRUE" : "FALSE"}</div>
            <div className="text-xs mt-1">{r.subject}</div>
          </div>
        </div>
      )}

      {r.status !== "loading" && (
        <>
          <div className="flex flex-wrap gap-1">
            <Badge variant="outline" className="text-[10px] gap-1"><Cpu className="h-3 w-3" />{modelLabel(r.model)}</Badge>
            {typeof r.durationMs === "number" && (
              <Badge variant="outline" className="text-[10px] gap-1"><Clock className="h-3 w-3" />{(r.durationMs / 1000).toFixed(1)}s</Badge>
            )}
          </div>
          {!warn && (
            <>
              <div className="space-y-1">
                <span className="text-[11px] font-medium uppercase text-muted-foreground">Reason</span>
                <p className="text-xs">{r.reason}</p>
              </div>
              <div className="space-y-1">
                <span className="text-[11px] font-medium uppercase text-muted-foreground">Message</span>
                <p className="text-xs">{r.message}</p>
              </div>
            </>
          )}
          {r.data && Object.keys(r.data).length > 0 && (
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(r.data).map(([k, v]) => (
                <div key={k} className="rounded-md bg-muted p-2 text-center">
                  <div className="text-lg font-bold leading-none">{v}</div>
                  <div className="text-[10px] text-muted-foreground truncate mt-1" title={k}>{k}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RunView({ run }: { run: TestRun }) {
  const done = run.results.filter((r) => r.status === "done");
  const trueCount = done.filter((r) => r.result?.toUpperCase() === "TRUE").length;
  const tags = useMemo(() => {
    const s = new Set<string>();
    run.results.forEach((r) => Object.keys(r.data ?? {}).forEach((k) => s.add(k)));
    return [...s];
  }, [run]);
  const chartData = tags.map((tag) => {
    const row: Record<string, string | number> = { tag };
    run.results.forEach((r, i) => { row[`Image ${i + 1}`] = r.data?.[tag] ?? 0; });
    return row;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge className="gap-1"><Cpu className="h-3 w-3" />{modelLabel(run.model)}</Badge>
        <span className="rounded-md bg-destructive text-destructive-foreground px-2 py-0.5 font-medium">TRUE {trueCount}</span>
        <span className="rounded-md bg-success text-success-foreground px-2 py-0.5 font-medium">FALSE {done.length - trueCount}</span>
        {run.results.length - done.length > 0 && (
          <span className="text-muted-foreground">{run.results.length - done.length} pending / not analysed</span>
        )}
      </div>

      {chartData.length > 0 && done.length > 0 && (
        <div className="h-48 rounded-lg border bg-background p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="tag" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {run.results.map((_, i) => (
                <Bar key={i} dataKey={`Image ${i + 1}`} fill={BAR_COLOURS[i % BAR_COLOURS.length]} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-5 gap-3">
        {run.results.map((r, i) => <ResultColumn key={i} r={r} index={i} />)}
      </div>
    </div>
  );
}

export function WorkflowTestComparison({ runs, onClear }: { runs: TestRun[]; onClear: () => void }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const latestId = runs[0]?.id ?? null;
  useEffect(() => { setSelectedId(null); }, [latestId]);
  const shown = runs.find((r) => r.id === selectedId) ?? runs[0];

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">Test results</span>
        <Button variant="outline" size="sm" onClick={onClear} className="h-7 text-xs">Clear</Button>
      </div>
      {runs.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground mr-1">Runs:</span>
          {runs.map((r) => {
            const on = shown.id === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelectedId(r.id)}
                className={cn("rounded-full border px-2.5 py-0.5 text-[11px]", on ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}
              >
                {modelLabel(r.model)} · {r.startedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </button>
            );
          })}
        </div>
      )}
      <RunView run={shown} />
    </div>
  );
}
